import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { serviceCallsApi } from '../services/api';

// Summary of every stage a service call can be waiting at, shared by the
// Service Pending, Service Completed and Service Follow-Up pages:
//   Unalloted → Pending → (Closed) Follow-up → Confirmed, or
//   Follow-up → Reopened → Closed again → Re-followup.
// Follows the page's Handler / Type filters. Clicking a card opens the page
// that lists those calls; a page can take over a click (Service Pending
// switches its own tab instead).

type Bucket = { total: number; over_day: number; oldest: string | null };
type Summary = Record<'unalloted' | 'pending' | 'followup' | 'refollowup', Bucket> & {
  completed: { today: number; month: number; total: number };
};
type CardKey = 'unalloted' | 'pending' | 'followup' | 'refollowup' | 'completed';

interface Props {
  staff?: string;
  type?: string;
  /** Change it to reload (e.g. the page's list after take / close / confirm). */
  refreshKey?: unknown;
  /** Per-card click override; otherwise the card opens its page. */
  onCardClick?: Partial<Record<CardKey, () => void>>;
}

const ageOf = (d: string) => {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(m / 60), days = Math.floor(h / 24);
  if (days > 0) return `${days}d`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}m`;
  return 'now';
};

const WAITING: { key: Exclude<CardKey, 'completed'>; title: string; hint: string; tone: string; path: string }[] = [
  { key: 'unalloted',  title: 'Unalloted',           hint: 'New call, nobody has taken it yet',                   tone: 'border-blue-200 bg-blue-50/60 text-blue-700',          path: '/service/pending' },
  { key: 'pending',    title: 'Pending',             hint: 'Taken — handler is working on it',                   tone: 'border-amber-200 bg-amber-50/60 text-amber-700',       path: '/service/pending' },
  { key: 'followup',   title: 'Follow-up Pending',   hint: 'Closed — call the customer to confirm it is solved', tone: 'border-emerald-200 bg-emerald-50/60 text-emerald-700', path: '/service/followup' },
  { key: 'refollowup', title: 'Re-followup Pending', hint: 'Reopened at follow-up, fixed again — confirm again', tone: 'border-rose-200 bg-rose-50/60 text-rose-700',          path: '/service/followup' },
];

const ServiceSummaryCards: React.FC<Props> = ({ staff, type, refreshKey, onCardClick }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<Summary | null>(null);

  useEffect(() => {
    let cancelled = false;
    serviceCallsApi.getPendingSummary(staff, type)
      .then(res => { if (!cancelled) setSummary(res.data); })
      .catch(() => { /* cards are extra — never block the page */ });
    return () => { cancelled = true; };
  }, [staff, type, refreshKey]);

  if (!summary) return null;
  const click = (key: CardKey, path: string) => () => (onCardClick?.[key] ? onCardClick[key]!() : navigate(path));

  return (
    <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 grid grid-cols-2 lg:grid-cols-5 gap-3 shrink-0">
      {WAITING.map(card => {
        const b = summary[card.key];
        return (
          <button key={card.key} type="button" onClick={click(card.key, card.path)} title={card.hint}
            className={`text-left rounded-lg border px-3 py-2 hover:shadow-sm transition-shadow ${card.tone}`}>
            <div className="text-[11px] font-bold uppercase tracking-wide">{card.title}</div>
            <div className="text-[10px] text-gray-500 mb-1 truncate">{card.hint}</div>
            <div className="space-y-0.5 text-[12px] text-gray-600">
              <div className="flex justify-between"><span>Total Calls</span><span className="font-semibold text-gray-900">{b.total}</span></div>
              <div className="flex justify-between"><span>Waiting over 24 hrs</span><span className={`font-semibold ${b.over_day > 0 ? 'text-red-600' : 'text-gray-900'}`}>{b.over_day}</span></div>
              <div className="flex justify-between"><span>Oldest</span><span className="font-semibold text-gray-900">{b.oldest ? ageOf(b.oldest) : '—'}</span></div>
            </div>
          </button>
        );
      })}
      {/* Completed — work finished (Closed + Confirmed), by the day it was closed */}
      <button type="button" onClick={click('completed', '/service/completed')} title="Work finished — same calls as the Service Completed page"
        className="text-left rounded-lg border px-3 py-2 hover:shadow-sm transition-shadow border-violet-200 bg-violet-50/60 text-violet-700">
        <div className="text-[11px] font-bold uppercase tracking-wide">Completed</div>
        <div className="text-[10px] text-gray-500 mb-1 truncate">Work finished (closed or confirmed)</div>
        <div className="space-y-0.5 text-[12px] text-gray-600">
          <div className="flex justify-between"><span>Today</span><span className="font-semibold text-gray-900">{summary.completed.today}</span></div>
          <div className="flex justify-between"><span>This Month</span><span className="font-semibold text-gray-900">{summary.completed.month}</span></div>
          <div className="flex justify-between"><span>All Time</span><span className="font-semibold text-gray-900">{summary.completed.total}</span></div>
        </div>
      </button>
    </div>
  );
};

export default ServiceSummaryCards;
